import { logger } from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, ShieldCheckIcon, LockIcon, AlertTriangleIcon, ArchiveIcon, CheckCircleIcon, BanIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { Role } from '../../types';
import { useToast } from '../../contexts/ToastContext';
type RoleStatus = 'active' | 'inactive' | 'archived';
interface RoleWithStatus extends Role {
  status: RoleStatus;
}
export function Roles() {
  const {
    showToast
  } = useToast();
  const {
    roles,
    addRole,
    updateRole,
    deleteRole
  } = useData();
  const [rolesWithStatus, setRolesWithStatus] = useState<RoleWithStatus[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<RoleWithStatus[]>([]);
  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<RoleStatus | 'all'>('active');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleWithStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  // Load role statuses from localStorage
  const getRoleStatuses = (): Record<string, RoleStatus> => {
    try {
      const saved = localStorage.getItem('savidna-role-statuses');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  };
  const saveRoleStatus = (roleId: string, status: RoleStatus) => {
    const statuses = getRoleStatuses();
    statuses[roleId] = status;
    localStorage.setItem('savidna-role-statuses', JSON.stringify(statuses));
  };
  // Initialize roles with status
  useEffect(() => {
    const statuses = getRoleStatuses();
    const mapped = roles.map(role => ({
      ...role,
      status: statuses[role.id] || 'active'
    }));
    setRolesWithStatus(mapped);
  }, [roles]);
  // Filter logic
  useEffect(() => {
    let result = rolesWithStatus;
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r => r.displayName.toLowerCase().includes(term) || r.name.toLowerCase().includes(term) || (r.description || '').toLowerCase().includes(term));
    }
    setFilteredRoles(result);
  }, [rolesWithStatus, statusFilter, searchTerm]);
  const handleStatusChange = (role: RoleWithStatus, newStatus: RoleStatus) => {
    if (role.isSystemRole) {
      showToast({
        type: 'warning',
        title: 'Acción no permitida',
        message: 'No se pueden modificar los roles del sistema.'
      });
      return;
    }
    saveRoleStatus(role.id, newStatus);
    setRolesWithStatus(prev => prev.map(r => r.id === role.id ? {
      ...r,
      status: newStatus
    } : r));
    // Persist to Supabase
    updateRole(role.id, {
      status: newStatus
    } as any).catch(e => logger.error('Error updating role status in Supabase:', e));
  };
  const openNewForm = () => {
    setSelectedRole(null);
    setFormData({
      name: '',
      displayName: '',
      description: ''
    });
    setFormErrors({});
    setSuccessMessage('');
    setIsFormOpen(true);
  };
  const openEditForm = (role: RoleWithStatus) => {
    if (role.isSystemRole) return;
    setSelectedRole(role);
    setFormData({
      name: role.name,
      displayName: role.displayName,
      description: role.description || ''
    });
    setFormErrors({});
    setSuccessMessage('');
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (role: RoleWithStatus) => {
    if (role.isSystemRole) return;
    setSelectedRole(role);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setSuccessMessage('');
    // Validation
    const errors: Record<string, string> = {};
    if (!formData.displayName.trim()) errors.displayName = 'El nombre visible es requerido';
    if (!selectedRole && !formData.name.trim()) errors.name = 'El nombre interno es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSubmitting(true);
    try {
      if (selectedRole) {
        await updateRole(selectedRole.id, {
          displayName: formData.displayName,
          description: formData.description
        });
        showToast({
          type: 'success',
          title: 'Rol actualizado',
          message: `${formData.displayName} fue actualizado correctamente.`
        });
      } else {
        await addRole({
          name: formData.name.toLowerCase().replace(/\s+/g, '_'),
          displayName: formData.displayName,
          description: formData.description,
          isSystemRole: false
        });
        showToast({
          type: 'success',
          title: 'Rol creado',
          message: `${formData.displayName} fue creado correctamente.`
        });
      }
      setIsFormOpen(false);
    } catch (error) {
      logger.error('Error saving role:', error);
      showToast({
        type: 'error',
        title: 'Error al guardar',
        message: 'No se pudo guardar el rol.'
      });
      setFormErrors({
        submit: 'Error al guardar el rol'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async () => {
    if (!selectedRole) return;
    setIsSubmitting(true);
    try {
      const roleName = selectedRole.displayName || selectedRole.name;
      await deleteRole(selectedRole.id);
      showToast({
        type: 'error',
        title: 'Rol eliminado',
        message: `${roleName} fue eliminado.`
      });
      setIsDeleteConfirmOpen(false);
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Error al eliminar',
        message: 'No se pudo eliminar el rol.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const getStatusLabel = (status: RoleStatus) => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'inactive':
        return 'Inactivo';
      case 'archived':
        return 'Archivado';
    }
  };
  return <div className="space-y-6" data-id="element-4601">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4602">
        <div data-id="element-4603">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4604">
            <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-4605" />
            Roles
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4606">
            Define los roles y niveles de acceso para los usuarios
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4608" />} onClick={openNewForm} data-id="element-4607">
          Nuevo Rol
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4" data-id="element-4609">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-4610">
          <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full md:w-auto" data-id="element-4611">
            {(['active', 'inactive', 'archived', 'all'] as const).map(status => <button key={status} onClick={() => setStatusFilter(status)} className={`
                  px-4 py-1.5 text-sm font-medium rounded-md transition-all flex-1 md:flex-none capitalize
                  ${statusFilter === status ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}
                `} data-id="element-4612">
                  {status === 'all' ? 'Todos' : getStatusLabel(status)}
                </button>)}
          </div>

          <div className="relative w-full md:w-64" data-id="element-4613">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4614" />
            <input type="text" placeholder="Buscar roles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all" data-id="element-4615" />
          </div>
        </div>
      </Card>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4616">
        {filteredRoles.length === 0 ? <Card className="col-span-full" data-id="element-4617">
            <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-4618">
              No se encontraron roles con los filtros seleccionados.
            </div>
          </Card> : filteredRoles.map(role => <Card key={role.id} className={`hover:shadow-lg transition-all duration-300 group ${role.status === 'archived' ? 'opacity-75 grayscale-[0.5]' : ''}`} data-id="element-4619">
              <div className="p-6 flex flex-col h-full" data-id="element-4620">
                <div className="flex items-start justify-between mb-4" data-id="element-4621">
                  <div className="flex items-center gap-3" data-id="element-4622">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${role.isSystemRole ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' : 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'}`} data-id="element-4623">
                      <ShieldCheckIcon className="w-6 h-6" data-id="element-4624" />
                    </div>
                    <div data-id="element-4625">
                      <h3 className="font-bold text-gray-900 dark:text-white text-lg" data-id="element-4626">
                        {role.displayName}
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-4627">
                        <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-500" data-id="element-4628">
                          {role.name}
                        </code>
                        {role.status !== 'active' && <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${role.status === 'inactive' ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'}`} data-id="element-4629">
                            {getStatusLabel(role.status)}
                          </span>}
                      </div>
                    </div>
                  </div>
                  {role.isSystemRole && <div title="Rol del sistema" data-id="element-4630">
                      <LockIcon className="w-4 h-4 text-gray-400" data-id="element-4631" />
                    </div>}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 flex-1 leading-relaxed" data-id="element-4632">
                  {role.description || 'Sin descripción disponible para este rol.'}
                </p>

                <div className="pt-4 border-t border-gray-100 dark:border-gray-700/50 flex items-center gap-2" data-id="element-4633">
                  {role.isSystemRole ? <div className="w-full text-center text-xs text-gray-400 italic py-2" data-id="element-4634">
                      Rol protegido del sistema
                    </div> : <>
                      <button onClick={() => openEditForm(role)} className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors flex-1 flex justify-center" title="Editar" data-id="element-4635">
                        <EditIcon className="w-4 h-4" data-id="element-4636" />
                      </button>

                      {role.status === 'active' ? <button onClick={() => handleStatusChange(role, 'inactive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors flex-1 flex justify-center" title="Desactivar" data-id="element-4637">
                          <BanIcon className="w-4 h-4" data-id="element-4638" />
                        </button> : <button onClick={() => handleStatusChange(role, 'active')} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors flex-1 flex justify-center" title="Activar" data-id="element-4639">
                          <CheckCircleIcon className="w-4 h-4" data-id="element-4640" />
                        </button>}

                      {role.status !== 'archived' ? <button onClick={() => handleStatusChange(role, 'archived')} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-1 flex justify-center" title="Archivar" data-id="element-4641">
                          <ArchiveIcon className="w-4 h-4" data-id="element-4642" />
                        </button> : <button onClick={() => openDeleteConfirm(role)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-1 flex justify-center" title="Eliminar permanentemente" data-id="element-4643">
                          <TrashIcon className="w-4 h-4" data-id="element-4644" />
                        </button>}
                    </>}
                </div>
              </div>
            </Card>)}
      </div>

      {/* Role Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => !isSubmitting && setIsFormOpen(false)} title={selectedRole ? 'Editar Rol' : 'Nuevo Rol'} size="md" icon={<ShieldCheckIcon className="w-5 h-5" data-id="element-4646" />} accentColor="purple" data-id="element-4645">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4647">
          {successMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm flex items-center gap-2" data-id="element-4648">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-4649" />
              {successMessage}
            </div>}

          <div data-id="element-4650">
            <div className="flex items-center gap-2 mb-4" data-id="element-4651">
              <span className="w-1.5 h-5 rounded-full bg-purple-500" data-id="element-4652"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4653">
                Identificación
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4654">
              {!selectedRole && <Input label="Nombre Interno (único)" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="ej: gerente_ventas" helperText="Solo letras minúsculas, números y guiones bajos" disabled={isSubmitting} data-id="element-4655" />}
              <Input label="Nombre Visible" value={formData.displayName} onChange={e => setFormData({
              ...formData,
              displayName: e.target.value
            })} error={formErrors.displayName} placeholder="ej: Gerente de Ventas" disabled={isSubmitting} data-id="element-4656" />
            </div>
          </div>

          <div data-id="element-4657">
            <div className="flex items-center gap-2 mb-4" data-id="element-4658">
              <span className="w-1.5 h-5 rounded-full bg-indigo-500" data-id="element-4659"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4660">
                Detalles
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4661">
              <Textarea label="Descripción del Rol" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Describe las responsabilidades de este rol..." disabled={isSubmitting} data-id="element-4662" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-4663">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} disabled={isSubmitting} data-id="element-4664">
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSubmitting} data-id="element-4665">
              {selectedRole ? 'Guardar Cambios' : 'Crear Rol'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => !isSubmitting && setIsDeleteConfirmOpen(false)} title="Eliminar Rol" size="sm" icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-4667" />} accentColor="rose" data-id="element-4666">
        <div className="space-y-4" data-id="element-4668">
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg" data-id="element-4669">
            <AlertTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" data-id="element-4670" />
            <div data-id="element-4671">
              <p className="text-sm font-medium text-red-800 dark:text-red-200" data-id="element-4672">
                ¿Estás seguro de que deseas eliminar este rol?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-4673">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2" data-id="element-4674">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} disabled={isSubmitting} data-id="element-4675">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isSubmitting} data-id="element-4676">
              Eliminar Rol
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}